/**
 * ObjectChip — a reference to another object as a small link chip, never an
 * inset card (docs/features/journey-visual-language/01_proposal.md §4, "Tab
 * content"): `PO-K20417 · PDQ ↗`, `Job 741057 ↗`, `INV-K30117 ↗`. A chip
 * with no page to open (a pick) is the same chip without the arrow.
 */
import { Link } from "react-router";
import { ArrowUpRight } from "lucide-react";
import { cn } from "../primitives/utils";

const chipClass =
  "inline-flex max-w-full items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs font-medium whitespace-nowrap";

export function ObjectChip({
  label,
  detail,
  to,
}: {
  /** Its number or name: "PO-K20417". */
  label: string;
  /** Who or where, after a dot: "PDQ". */
  detail?: string;
  /** Its page; without one the chip is plain text. */
  to?: string;
}) {
  const content = (
    <>
      <span className="truncate">
        {label}
        {detail && <span className="text-muted-foreground"> · {detail}</span>}
      </span>
      {to && <ArrowUpRight aria-hidden className="size-3 shrink-0" />}
    </>
  );
  if (!to) return <span className={chipClass}>{content}</span>;
  return (
    <Link
      to={to}
      className={cn(
        chipClass,
        "text-foreground outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      {content}
    </Link>
  );
}
